<template>
	<div class="user-center-container personal-company-page">
		<div class="user-center-content">
			<div class="page-heading">
				<div>
					<h2>企业认证信息</h2>
					<p>查看个人账号的企业授权及认证状态</p>
				</div>
				<span :class="['authorization-status', isAuthorized ? 'is-authorized' : 'is-pending']">
					<i :class="isAuthorized ? 'el-icon-success' : 'el-icon-time'"></i>
					{{ isAuthorized ? '已认证' : '待授权' }}
				</span>
			</div>

			<div v-if="isAuthorized" class="bound-company-card">
				<div class="company-icon"><i class="el-icon-office-building"></i></div>
				<div class="bound-company-info">
					<div class="bound-title">
						<h3>{{ companyInfo.companyName }}</h3>
						<span>当前绑定企业</span>
					</div>
					<div class="bound-fields">
						<div>
							<span>统一社会信用代码</span>
							<strong>{{ companyInfo.companyCode }}</strong>
						</div>
						<div>
							<span>授权时间</span>
							<strong>{{ formatAuthorizationDate(companyInfo.companyAuthTime) }}</strong>
						</div>
					</div>
				</div>
				<el-button type="danger" plain :loading="authorizationLoading || unbindLoading" @click="handleUnbind">
					解除绑定
				</el-button>
			</div>

			<div v-else class="auth-guide-card">
				<div class="guide-copy">
					<h3>如何获得企业认证？</h3>
					<p>
						请联系您所属企业的管理员，让其通过
						<span>【企业授权】</span>
						页面输入您的身份证号或者手机号进行授权。授权后即完成企业认证。
					</p>
					<div class="guide-tip">
						<i class="el-icon-info"></i>
						个人用户不能自行发起企业认证申请
					</div>
				</div>
			</div>

			<company-auth-rules></company-auth-rules>
			<company-faq></company-faq>
		</div>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex'
	import { parseTime } from '@/utils'
	import { getAuthorizedUserList, unbindAuthorizedUser } from '@/api/userCenter/companyManagement'
	import CompanyAuthRules from './components/AuthRules'
	import CompanyFaq from './components/CompanyFaq'

	export default {
		name: 'PersonalCompany',
		components: { CompanyAuthRules, CompanyFaq },
		data() {
			return {
				localUnbound: false,
				// 当前个人用户最新一条已授权记录，解绑时使用其中的授权记录 ID。
				authorizationRecord: {},
				// 授权记录查询期间锁定解绑入口，避免记录尚未返回就提交解绑。
				authorizationLoading: false,
				// 个人用户提交解绑期间锁定按钮，避免重复请求。
				unbindLoading: false
			}
		},
		computed: {
			...mapGetters(['userInfo']),
			// 用户信息中存在 companyId，代表当前个人用户已获得企业授权。
			isAuthorized() {
				if (this.localUnbound) return false
				return Boolean(this.userInfo.companyId)
			},
			companyInfo() {
				return {
					companyName: this.userInfo.companyName,
					companyCode: this.userInfo.companyId,
					// 授权时间优先取授权记录，兼容用户信息中已有的授权时间字段。
					companyAuthTime: this.authorizationRecord.authorizeTime || this.userInfo.companyAuthTime || '',
					// userInfo 不包含授权记录 ID，解绑必须使用单独查询到的授权记录 ID。
					authorizationId: this.authorizationRecord.authorizationId || ''
				}
			}
		},
		async mounted() {
			if (this.isAuthorized) await this.getAuthorizationRecord()
		},
		methods: {
			/** 授权时间只展示年月日，隐藏接口返回的时分秒。 */
			formatAuthorizationDate(value) {
				return parseTime(value, '{y}-{m}-{d}') || '--'
			},
			/** 查询当前个人用户最新一条状态为“已授权”的授权记录。 */
			async getAuthorizationRecord() {
				this.authorizationLoading = true
				try {
					const response = await getAuthorizedUserList({
						page: 1,
						limit: 1,
						status: '1'
					})
					// 该接口为分页查询，授权记录集合固定从 data.records 中读取。
					const records = (response.data && response.data.records) || []
					this.authorizationRecord = records[0] || {}
				} finally {
					this.authorizationLoading = false
				}
			},

			/**
			 * 个人用户主动解除企业授权。
			 * 解绑时间限制由接口统一校验，页面只负责二次确认和提交授权记录 ID。
			 */
			handleUnbind() {
				if (!this.companyInfo.authorizationId) {
					this.$modal.msgWarning('未获取到企业授权记录，请刷新页面后重试')
					return
				}

				this.$modal
					.confirm('确认解除当前企业绑定吗？解绑后需由企业管理员重新授权。')
					.then(async () => {
						this.unbindLoading = true
						try {
							await unbindAuthorizedUser({ authorizationId: this.companyInfo.authorizationId })
							this.authorizationRecord = {}
							this.localUnbound = true
							this.$message.success('已解除企业绑定')
							// 同步刷新全局用户信息；刷新失败不影响已经成功的解绑结果。
							this.$store.dispatch('user/getUserInfo').catch(() => {})
						} finally {
							this.unbindLoading = false
						}
					})
					.catch(() => {})
			}
		}
	}
</script>

<style lang="scss" scoped>
	@import '~@/views/user/styles/user-center.scss';

	.page-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding-bottom: 18px;
		border-bottom: 1px solid #e8ebef;

		h2 {
			margin: 0 0 6px;
			color: #303846;
			font-size: 20px;
		}

		p {
			margin: 0;
			color: #929aa6;
			font-size: 13px;
		}
	}

	.authorization-status {
		display: inline-flex;
		align-items: center;
		padding: 7px 14px;
		border-radius: 18px;
		font-size: 14px;
		font-weight: 500;

		i {
			margin-right: 6px;
		}

		&.is-pending {
			color: #b47820;
			background: #fff3dc;
		}

		&.is-authorized {
			color: #188957;
			background: #e1f5ea;
		}
	}

	.auth-guide-card,
	.bound-company-card {
		display: flex;
		align-items: center;
		margin-top: 22px;
		padding: 28px 30px;
		border-radius: 10px;
	}

	.auth-guide-card {
		border: 1px solid #b8d7fa;
		background: linear-gradient(135deg, #eef6ff, #ffffff 72%);
	}

	.guide-copy {
		width: 100%;
		min-width: 0;

		h3 {
			margin: 0 0 12px;
			color: #26364a;
			font-size: 19px;
		}

		p {
			margin: 0;
			color: #5d6a7b;
			font-size: 14px;
			line-height: 25px;

			span {
				color: #2671cb;
				font-weight: 500;
			}
		}
	}

	.guide-tip {
		margin-top: 12px;
		color: #8a7242;
		font-size: 13px;

		i {
			margin-right: 5px;
			color: #e6a23c;
		}
	}

	.bound-company-card {
		border: 1px solid #a9d5bb;
		background: linear-gradient(135deg, #eefaf3, #ffffff 72%);
	}

	.company-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 68px;
		height: 68px;
		margin-right: 20px;
		border-radius: 15px;
		color: #ffffff;
		font-size: 32px;
		background: #209660;
	}

	.bound-company-info {
		min-width: 0;
		margin-right: 20px;
	}

	.bound-title {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 10px;

		h3 {
			margin: 0;
			color: #2e3d4f;
			font-size: 19px;
		}

		span {
			padding: 3px 9px;
			border-radius: 12px;
			color: #188957;
			font-size: 12px;
			background: #dcf2e6;
		}
	}

	.bound-fields {
		display: flex;
		gap: 32px;
		margin-top: 14px;

		span,
		strong {
			display: block;
		}

		span {
			margin-bottom: 5px;
			color: #89948f;
			font-size: 12px;
		}

		strong {
			color: #48564f;
			font-size: 14px;
		}
	}

	.bound-company-card > .el-button {
		margin-left: auto;
	}
</style>
